import { Suspense, lazy } from 'react'
import { Text } from '@mantine/core'
import { isKnownScreen, resolveCar } from '@racedash/shared'
import type { Screen, ScreenEntry } from '@racedash/shared'
import DashboardGrid from './DashboardGrid'

/**
 * Loaded only when someone actually edits.
 *
 * The viewer is what runs unattended on a pit wall and what a public share link
 * serves, and neither has any use for the editor — so it stays out of both
 * bundles rather than being shipped and never reached.
 */
const DashboardEditor = lazy(() => import('./edit/DashboardEditor'))
import TimingScreen from '../screens/timing/TimingScreen'
import StintsScreen from '../screens/stints/StintsScreen'
import LapsScreen from '../screens/laps/LapsScreen'
import PlannerScreen from '../screens/planner/PlannerScreen'
import FuelScreen from '../screens/fuel/FuelScreen'
import type { DashboardData } from './useSessionData'
import type { ScreenContext } from './screenContext'

/**
 * Render one screen.
 *
 * The only place that switches on screen kind. Everything above it — tabs,
 * source binding, dirty tracking — treats a screen as a screen, which is what
 * keeps adding a kind to one file plus one branch.
 */
export default function ScreenView({
  screen,
  editing,
  onChange,
  data,
  primaryCar,
  now,
  context,
}: {
  screen: ScreenEntry
  editing: boolean
  onChange: (next: Screen) => void
  data: DashboardData
  primaryCar: string | null
  now: Date
  context: ScreenContext
}) {
  if (!isKnownScreen(screen)) {
    return (
      <div className="dash-empty">
        <Text c="dimmed" size="sm" ta="center">
          This screen — “{screen.unknownKind}” — was made by a newer version of RaceDash. It is
          still here, and saving will not remove it.
        </Text>
      </div>
    )
  }

  // A screen may pin its own car; otherwise it follows the dashboard's. Same
  // escape hatch a widget has, one level up, which is what lets two grids sit
  // side by side following two cars under one share link.
  const car = resolveCar(screen.car, primaryCar)

  switch (screen.config.kind) {
    case 'grid': {
      const layout = screen.config.layout
      return editing ? (
        <Suspense fallback={<div className="dash-empty">Loading the editor…</div>}>
          <DashboardEditor
            layout={layout}
            onChange={(next) => onChange({ ...screen, config: { kind: 'grid', layout: next } })}
            data={data}
            primaryCar={car}
            now={now}
            context={context}
          />
        </Suspense>
      ) : (
        <DashboardGrid layout={layout} data={data} primaryCar={car} now={now} context={context} />
      )
    }
    case 'timing':
      return (
        <TimingScreen
          config={screen.config}
          data={data}
          car={car}
          now={now}
          screenId={screen.id}
          context={context}
        />
      )
    case 'stints':
      return (
        <StintsScreen
          data={data}
          car={car}
          now={now}
          context={context}
          screenId={screen.id}
        />
      )
    case 'laps':
      return (
        <LapsScreen
          data={data}
          car={car}
          context={context}
          screenId={screen.id}
          // For the chart that compares one car against another, which needs
          // laps this screen's own listener never asked for.
          sessionId={data.sessionId}
        />
      )
    case 'planner':
      return <PlannerScreen data={data} car={car} now={now} context={context} />
    case 'fuel':
      return <FuelScreen data={data} car={car} context={context} />
  }
}
